import React, { useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useMeasure, useDebounce } from 'react-use'
import { FormattedMessage } from 'react-intl'
import { Table, Button, Tooltip } from 'antd'
import { ClusterOutlined, SearchOutlined } from '@ant-design/icons'
import {
  pinRowSelection,
  summaryColSpan,
  renderPinoutSummary,
} from './PinoutSelection'
import Pin from './Pin'
import PinEmpty from './PinEmpty'
import { strategyFactory } from '../selectors/pinout'
import {
  CONNECTOR_PART,
  useRelatedContentEnabledFor,
  useTools,
} from '../hooks/RelatedContent'
import { ToolsButton } from './RelatedContent'
import { useUserInfo } from '../hooks/User'
import { useCheckPermission } from '../hooks/Permissions'
import {
  RelatedDiagramsButton,
  needsPinsForRelatedDiagrams,
} from '../FunctionalDiagrams/RelatedDiagramsModal'
import { setRelatedDiagramsModal } from '../actions/related-diagrams-modal-actions'

import { ReactComponent as GroundIcon } from '../assets/images/icons/svg/pinout/GND.svg'
import { ReactComponent as PowerIcon } from '../assets/images/icons/svg/pinout/PWR.svg'
import { ReactComponent as CANBusIcon } from '../assets/images/icons/svg/pinout/CAN.svg'
import { DARK_MODE, FUNCTIONAL_DIAGRAMS_FLAG } from '../constants'

const HEADER_HEIGHT = 50
const SUMMARY_ROW_HEIGHT = 50

function getCircuitIcon(circuitCategory) {
  const iconConfig = {
    GND: GroundIcon,
    PWR: PowerIcon,
    CAN: CANBusIcon,
  }
  return iconConfig[circuitCategory]
}

function pinFormatter(content) {
  const pin = content ? <Pin colors={content} /> : <PinEmpty />
  return <div>{pin}</div>
}

function categoryFormatter(content, row) {
  const CircuitIcon = getCircuitIcon(content)
  if (CircuitIcon) {
    return (
      <div
        key={crypto.randomUUID()}
        style={{ height: '25px', position: 'relative' }}
      >
        <CircuitIcon className="circuit-category-img" />
      </div>
    )
  } else {
    return null
  }
}

const tracePinFormatterFactory =
  (vin, selectedElement, onPinClick) => (content, row) => {
    const actionContent = []
    const pinClickHandler = () => {
      onPinClick(row, vin, selectedElement, row.originalCavityNumber)
    }

    if (content) {
      actionContent.push(
        <Button
          key={`circuit-details-${row.originalCavityNumber}`}
          onClick={pinClickHandler}
        >
          <FormattedMessage id="pinout.buttons.circuitDetails" />
        </Button>,
      )
    }

    return <div>{actionContent}</div>
  }

const hairlineWidth = 1
const centerLineMargin = 3

const VerticalLine = ({ height, iconColors }) => {
  const styles = {
    width: hairlineWidth,
    height,
    border: `solid 1px ${iconColors}`,
    marginLeft: centerLineMargin,
  }
  return <div style={styles} />
}

const Circle = ({ iconColors }) => {
  const centerCirclePadding = 2
  const styles = {
    paddingRight: centerCirclePadding,
    borderRadius: 4,
    width: 8,
    height: 8,
    backgroundColor: iconColors,
  }
  return <div style={styles} />
}

function JumperConnection({ iconColors }) {
  return (
    <>
      <Circle iconColors={iconColors} />
      <VerticalLine height={50} iconColors={iconColors} />
      <Circle iconColors={iconColors} />
    </>
  )
}

function PinTable({
  vin,
  selectedElement,
  onPinClick,
  showConnectorModal = () => {},
  showEmptyPinsToggle = true,
  showEmptyPinsDefault = false,
  enablePinSelection = false,
  open,
  theme,
}) {
  const [containerRef, { height: containerHeight }] = useMeasure()
  const [showEmptyPins, setShowEmptyPins] = React.useState(showEmptyPinsDefault)
  const [tableHeight, setTableHeight] = React.useState(0)
  const [selectedPins, setSelectedPins] = React.useState([])
  const iconColors = theme != DARK_MODE ? '#000000' : '#ffffff'
  const dispatch = useDispatch()
  const checkPermission = useCheckPermission()
  const { userInfo } = useUserInfo()
  // Same gate every other Related Content surface uses: the feature on, and the
  // provider actually serving connector tooling.
  const isRelatedContentEnabled = useRelatedContentEnabledFor(CONNECTOR_PART)
  const canViewFunctionalDiagrams =
    (userInfo?.feature_flags || []).includes(FUNCTIONAL_DIAGRAMS_FLAG) &&
    checkPermission('page-functional-diagrams:get')
  // No category is excluded. Whatever the pinout belongs to, its picked pins are
  // resolved to the connector pins a diagram can be built from — the pin itself for
  // a connector or a power distribution box, the device's own connector for a
  // device, the connector on its own side for an inline, and every branch for a
  // splice.
  const isPinSelectionEnabled =
    enablePinSelection &&
    canViewFunctionalDiagrams &&
    !!vin &&
    !!selectedElement?.id
  // Circuit Details and Related Diagrams are alternatives, never both. A user with
  // functional diagrams gets what is in production today — pin selection and the
  // Related Diagrams button; everyone else keeps the Circuit Details column, which
  // opens `PinDestination` with the component details, the harness view and the
  // Fuses and Relays tab. Keyed on canViewFunctionalDiagrams rather than the flag
  // alone, so a user holding the flag without `page-functional-diagrams:get` is left
  // with the old route in rather than with neither.
  const showCircuitDetails = !canViewFunctionalDiagrams
  const showSummary = showEmptyPinsToggle || isPinSelectionEnabled
  const { isLoading: toolsLoading, tools } = useTools({
    vin,
    componentId: selectedElement?.id,
    enabled: isRelatedContentEnabled,
  })

  // containerHeight changes its height multiple times during re-render
  // but only the largest (last) value is needed.
  useDebounce(
    () => {
      setTableHeight(
        containerHeight -
          HEADER_HEIGHT -
          (showSummary ? SUMMARY_ROW_HEIGHT : 0),
      )
    },
    1,
    // showSummary is derived from asynchronous inputs — user info, permissions,
    // the selected component — so it can flip long after the height has settled.
    // Without it here the table keeps a scroll height that no longer matches
    // what is rendered, and clips its last row.
    [containerHeight, showSummary],
  )

  // Pin numbers are only meaningful for the component they belong to. Keeping
  // the same array when there is nothing to clear avoids a re-render on mount.
  useEffect(() => {
    setSelectedPins((current) => (current.length === 0 ? current : []))
  }, [selectedElement?.id])

  useEffect(() => {
    const pinTable = document.getElementById('pin-table')
    if (open) {
      pinTable.style.width = 'calc(100% - 278px)'
    } else {
      pinTable.style.width = '100%'
    }
  }, [open])

  const { pins, jumperPins } = strategyFactory(selectedElement)(
    selectedElement,
    showEmptyPins,
  )
  const tracerPinFormatter = tracePinFormatterFactory(
    vin,
    selectedElement,
    onPinClick,
  )
  // Not every component ships a connector drawing; without one there is nothing for
  // the column to open, so it is left out rather than filled with dead buttons.
  const connectorFileName = selectedElement?.images?.connector_file_name

  const columns = [
    {
      title: '',
      key: 'jumper',
      render: (text, record, index) => {
        return record.is_jumper && index % 2 === 0 ? (
          <JumperConnection iconColors={iconColors} />
        ) : null
      },
      width: '5%',
      onCell: (record, index) => {
        if (record.is_jumper) {
          if (index % 2 === 0) {
            return { rowSpan: 2 }
          } else {
            return { rowSpan: 0 }
          }
        }
        return {}
      },
    },
    {
      title: <FormattedMessage id="pinout.columns.circuit" />,
      dataIndex: 'circuit',
      key: 'circuit',
      width: '15%',
    },
    {
      title: <FormattedMessage id="pinout.columns.pin" />,
      key: 'cavity',
      render: (text, record, index) => (
        <div style={{ display: 'inline-block' }}>{record.cavity}</div>
      ),
      width: '10%',
    },
    {
      title: '',
      key: 'colors',
      render: (text, record, index) => pinFormatter(record.colors),
      width: '7%',
    },
    {
      title: <FormattedMessage id="pinout.columns.color" />,
      dataIndex: 'color_desc',
      key: 'color',
      width: '9%',
    },
    {
      title: '',
      key: 'category',
      render: (text, record, index) =>
        categoryFormatter(record.circuit_category, record),
      width: '9%',
      ellipsis: true,
    },
    {
      title: <FormattedMessage id="pinout.columns.signalDescription" />,
      dataIndex: 'signal_desc',
      key: 'signalDescription',
      // No width: this is the column worth the leftover room, so it takes whatever
      // the fixed columns beside it leave — more of it when Circuit Details is not
      // shown.
      ellipsis: true,
    },
    ...(isRelatedContentEnabled
      ? [
          {
            title: <FormattedMessage id="pinout.columns.tools" />,
            key: 'tools',
            // One icon button wide, so the space goes to the signal description
            // instead of being shared out evenly.
            width: '10%',
            render: (text, record) => {
              return (
                <ToolsButton
                  tools={[
                    ...(tools?.[record.cavity] || []),
                    ...(tools?.all || []),
                  ]}
                  pin={{ pin: record.cavity, description: record.signal_desc }}
                  isLoading={toolsLoading}
                />
              )
            },
          },
        ]
      : []),
    ...(connectorFileName
      ? [
          {
            title: <FormattedMessage id="pinout.columns.connector" />,
            key: 'connector',
            // One icon button wide, like the tools column beside it.
            width: '10%',
            render: (text, record) =>
              // An empty cavity has no pin number to highlight, so it gets no button.
              record.originalCavityNumber ? (
                <Tooltip
                  title={<FormattedMessage id="pinout.buttons.viewConnector" />}
                >
                  <Button
                    icon={<ClusterOutlined />}
                    data-testid="view-connector-button"
                    onClick={() =>
                      showConnectorModal(
                        [record.originalCavityNumber],
                        connectorFileName,
                      )
                    }
                  />
                </Tooltip>
              ) : null,
          },
        ]
      : []),
    ...(showCircuitDetails
      ? [
          {
            title: '',
            key: 'action',
            render: (text, record, index) =>
              tracerPinFormatter(record.originalCavityNumber, record),
          },
        ]
      : []),
  ]
  // Attach a stable, unique key per row. Important to avoid remounting rows on
  // every re-render (a changing key tears down and rebuilds each row).
  const allPins = [...pins, ...jumperPins].map((pin, index) => ({
    ...pin,
    rowKey: `${pin.cavity ?? ''}::${pin.circuit ?? ''}::${index}`,
  }))

  // Turning "All Pins" off drops rows, and a pin the user can no longer see must
  // not stay selected — it would still reach the related diagrams as an invisible
  // pin nobody chose. Keyed on the visible pins so it only runs when they change.
  const visiblePins = allPins.map((record) => record.originalCavityNumber)
  const visiblePinsKey = visiblePins.join('|')

  useEffect(() => {
    setSelectedPins((current) => {
      const visible = new Set(visiblePins.filter(Boolean))
      const next = current.filter((pin) => visible.has(pin))
      return next.length === current.length ? current : next
    })
    // visiblePins is rebuilt every render, so the key stands in for it here on
    // purpose — depending on the array itself would run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePinsKey])

  const rowSelection = isPinSelectionEnabled
    ? pinRowSelection({
        rows: allPins,
        rowKey: 'rowKey',
        getPin: (record) => record.originalCavityNumber,
        selectedPins,
        onPinsChange: setSelectedPins,
        // This table is not paginated, so a header checkbox would put every pin
        // of the connector into the related diagram in one click. That request
        // costs a whole-vehicle path search per selected pin, and "all pins" is
        // really the unfiltered detail diagram rather than a pin selection.
        // Picking a range deliberately is two clicks with shift.
        hideSelectAll: true,
      })
    : undefined

  return (
    <div id="pin-table" ref={containerRef}>
      <Table
        data-testid="pinout-table"
        summary={() =>
          showSummary &&
          renderPinoutSummary({
            colSpan: summaryColSpan(columns, isPinSelectionEnabled),
            isAllPins: showEmptyPins,
            setIsAllPins: setShowEmptyPins,
            showAllPinsToggle: showEmptyPinsToggle,
            children: isPinSelectionEnabled && (
              <RelatedDiagramsButton
                disabled={
                  needsPinsForRelatedDiagrams(selectedElement?.category) &&
                  selectedPins.length === 0
                }
                icon={<SearchOutlined />}
                showModal={() =>
                  dispatch(
                    setRelatedDiagramsModal({
                      vin,
                      componentId: selectedElement.id,
                      pins: selectedPins,
                    }),
                  )
                }
              />
            ),
          })
        }
        columns={columns}
        rowSelection={rowSelection}
        dataSource={allPins ? allPins : []}
        pagination={false}
        scroll={{
          y: allPins && allPins.length != 0 ? tableHeight : 150,
        }}
        size="middle"
        rowKey="rowKey"
        sticky
      />
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  return {
    theme: state.app.theme,
    ...ownProps,
  }
}

export default connect(mapStateToProps)(PinTable)
